import React, { Fragment, useEffect, useState } from 'react';

import OrganigramCanvasPlaceholder from 'Components/workforce/OrganigramCanvasPlaceholder';
import UnassignedPositionsPanel from 'Components/workforce/UnassignedPositionsPanel';
import WorkforceTypeFilterPanel from 'Components/workforce/WorkforceTypeFilterPanel';

import SelectInput from 'CommonComponents/SelectInput';

import useOrganigramFilter from 'Hooks/useOrganigramFilter';
import useUnassignedPanel from 'Hooks/useUnassignedPanel';

import OrgChart from 'Scripts/template';

export default function WorkforceOrganigramByDepartment({
    fetchDepartmentsUrl,
    fetchAllDepPositionsTreeUrl,
    fetchDepPositionsTreeUrl,
    fetchCcPositionsTreeUrl,
    fetchDepUnassignedUrl,
    fetchCcUnassignedUrl,
    treeUpdateUrl,
    eventId,
    initialFilterState = {},
    fullPreferenceValue = {},
    exportContext = {},
}) {
    // True from the outset: this always fetches on mount, and starting false
    // renders the chart's own "no positions" state for a beat before the first
    // response lands, which reads as an empty department rather than a loading one.
    const [loading, setLoading] = useState(true);
    const [departments, setDepartments] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [positions, setPositions] = useState([]);
    const [selectedDep, setSelectedDep] = useState(null);
    const [selectedCc, setSelectedCc] = useState(null);
    const [collapseLevel, setCollapseLevel] = useState(3);
    const [currentMaxCollapseLevel, setCurrentMaxCollapseLevel] = useState(3);
    const [showingAllDepartments, setShowingAllDepartments] = useState(false);
    const [updatesCompleted, setUpdatesCompleted] = useState(false);

    const preferenceKey = `dept_${eventId}`;
    const { filterState, setFilterState, filteredNodes } = useOrganigramFilter({
        initialFilterState: initialFilterState[preferenceKey],
        fullPreferenceValue,
        preferenceKey,
        nodes: positions,
    });

    // The panel follows the dropdown. "All Departments" loads a different,
    // deliberately ungated route, so that selection gets no panel and no
    // exclusion — its positions must stay visible somewhere.
    const unassignedUrl = showingAllDepartments
        ? ''
        : selectedCc
          ? (fetchCcUnassignedUrl || '').replace('DEP_SLUG', selectedDep?.slug ?? '').replace('CC_ID', selectedCc.id)
          : selectedDep
            ? (fetchDepUnassignedUrl || '').replace('DEPARTMENT_SLUG', selectedDep.slug)
            : '';

    const panel = useUnassignedPanel({
        unassignedUrl,
        treeUpdateUrl,
        onAssigned: () => fetchPositions(),
    });

    useEffect(() => {
        fetchDepartments();
    }, []);

    useEffect(() => {
        if (departments?.length) {
            setUpdatesCompleted(true);
            setSelectedDep(departments[0]);
        }
    }, [departments]);

    useEffect(() => {
        if (updatesCompleted) {
            showingAllDepartments ? fetchAllDepartmentPositions() : fetchPositions();
        }
    }, [selectedCc, selectedDep, collapseLevel]);

    const fetchDepartments = () => {
        axios
            .get(fetchDepartmentsUrl, {
                params: { loadCostcenters: true },
            })
            .then(({ data }) => {
                setDepartments(data.departments);
                setCostCenters([]);
                setSelectedCc(null);

                // Only a department leads on to a positions fetch, and only that
                // fetch clears the flag. With none, nothing follows, so release it
                // here or the canvas waits on a request that will never be made.
                if (!data.departments?.length) {
                    setLoading(false);
                }
            })
            .catch((e) => {
                console.log(e);
                setLoading(false);
            });
    };

    const fetchDepartmentPositions = (refetch = true) => {
        axios
            .get(fetchDepPositionsTreeUrl.replace('DEPARTMENT_SLUG', selectedDep.slug), {
                params: { currentMaxCollapseLevel: currentMaxCollapseLevel, ...panel.treeParams },
            })
            .then(({ data }) => {
                const { data: positions } = data;
                refetch ? setPositions(positions) : setPositions((oldPositions) => [...oldPositions, ...positions]);
                setCurrentMaxCollapseLevel(data.currentMaxCollapseLevel);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    };

    const fetchAllDepartmentPositions = (refetch = true) => {
        axios
            .get(fetchAllDepPositionsTreeUrl, {
                params: {
                    currentMaxCollapseLevel: currentMaxCollapseLevel,
                },
            })
            .then(({ data }) => {
                const { data: positions } = data;
                refetch ? setPositions(positions) : setPositions((oldPositions) => [...oldPositions, ...positions]);
                setCurrentMaxCollapseLevel(data.currentMaxCollapseLevel);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    };

    const fetchCostCenterPositions = (refetch = true) => {
        axios
            .get(fetchCcPositionsTreeUrl.replace('DEP_SLUG', selectedDep.slug).replace('CC_ID', selectedCc.id), {
                params: { currentMaxCollapseLevel: currentMaxCollapseLevel, ...panel.treeParams },
            })
            .then(({ data }) => {
                const { data: positions } = data;
                refetch ? setPositions(positions) : setPositions((oldPositions) => [...oldPositions, ...positions]);
                setCurrentMaxCollapseLevel(data.currentMaxCollapseLevel);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    };

    const fetchPositions = (refetch = true) => {
        if (selectedCc) {
            setLoading(true);
            fetchCostCenterPositions(refetch);
        } else if (selectedDep) {
            setLoading(true);
            fetchDepartmentPositions(refetch);
        }
    };

    const changeSelect = (value) => {
        setUpdatesCompleted(false);
        setCollapseLevel(3);

        if (value.startsWith('department-')) {
            setSelectedCc(null);
            const slug = value.slice('department-'.length);
            setUpdatesCompleted(true);
            if (slug === 'all') {
                setLoading(true);
                setShowingAllDepartments(true);
                fetchAllDepartmentPositions();
            } else {
                setShowingAllDepartments(false);
                setSelectedDep(departments?.find((dep) => dep.slug === slug));
            }
        } else if (value.startsWith('costcenter-')) {
            setShowingAllDepartments(false);
            const parts = value.split('-slug-'); // Split based on '-slug-'

            if (parts.length === 2) {
                // Ensure that there are two parts
                const costcenterId = parts[0].replace('costcenter-', ''); // Extract costcenter id
                const departmentSlug = parts[1];
                const currentDep = departments?.find((dep) => dep.slug === departmentSlug);
                setSelectedDep(currentDep);
                setUpdatesCompleted(true);
                setSelectedCc(currentDep.costcenters?.find((cc) => cc.id == costcenterId));
            }
        }
    };

    const changeCollapse = (value) => {
        setUpdatesCompleted(false);
        setUpdatesCompleted(true);
        setCollapseLevel(value);
    };

    // Identity for the PDF export's header, key and breadcrumb. Unlike the
    // department and cost centre surfaces, the scope here is picked in the
    // dropdown rather than fixed by the page.
    const chartExportContext = {
        ...exportContext,
        scope: showingAllDepartments ? $t('All Departments') : [selectedDep?.name, selectedCc?.name].filter(Boolean).join(' · ') || exportContext.scope,
    };

    return (
        <div className="tw-space-y-4 tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex tw-items-center tw-gap-x-3">
                <div className="tw-flex-grow">
                    <SelectInput
                        label={$t('Department')}
                        value={
                            showingAllDepartments
                                ? 'department-all'
                                : selectedCc
                                  ? 'costcenter-' + selectedCc?.id + '-slug-' + selectedDep?.slug
                                  : 'department-' + selectedDep?.slug
                        }
                        onChange={(e) => changeSelect(e.target.value)}
                    >
                        <option value="">{$t('Department Organigram')}</option>
                        <option value="department-all">{$t('All Departments Organigram')}</option>
                        {departments.map((dep) => {
                            return (
                                <Fragment key={dep.slug}>
                                    <option key={'department-' + dep.id} value={'department-' + dep.slug}>
                                        {dep.name}
                                    </option>
                                    {dep.costcenters.map((cc) => {
                                        return (
                                            <option className="!tw-ml-4" key={'costcenter-' + cc.id} value={'costcenter-' + cc.id + '-slug-' + dep.slug}>
                                                {cc.name} - {dep.name}
                                            </option>
                                        );
                                    })}
                                </Fragment>
                            );
                        })}
                    </SelectInput>
                </div>
                <div className="tw-flex-grow">
                    <SelectInput label={$t('Collapse Level')} value={collapseLevel} disabled={!selectedDep} onChange={(e) => changeCollapse(e.target.value)}>
                        {Array.from({ length: currentMaxCollapseLevel }, (_, index) => (
                            <option key={index + 1} value={index + 1}>
                                Level {index + 1}
                            </option>
                        ))}
                    </SelectInput>
                </div>
            </div>
            {/* The key stays outside the loading branch: it explains the chart, and
                it is readable before the chart it explains has arrived. */}
            <WorkforceTypeFilterPanel filterState={filterState} onChange={setFilterState} />
            {/* The panel is positioned rather than laid out beside the chart: as a
                flex sibling its rows would set the row height and stretch the canvas. */}
            <div className="tw-relative tw-flex tw-h-full tw-min-h-0">
                <div className="tw-h-full tw-w-full tw-min-w-0" style={{ paddingInlineEnd: panel.gutter }}>
                    {loading ? (
                        <OrganigramCanvasPlaceholder />
                    ) : (
                        <OrgChart
                            nodes={filteredNodes}
                            treeUpdateUrl={treeUpdateUrl}
                            collapseLevel={collapseLevel}
                            withoutPagination={true}
                            exportContext={chartExportContext}
                            {...panel.chartProps}
                        />
                    )}
                </div>
                {panel.enabled && <UnassignedPositionsPanel {...panel.panelProps} />}
            </div>
        </div>
    );
}
